<script setup>
// Dialog de install da galeria (Fase 2): o formulário vem do playbook_schema do template (slots_form do catálogo) —
// slots de texto obrigatórios sem default; com default vêm preenchidos. Texto longo (max_length > 240) vira textarea.
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'dashboard/components-next/polaris/ui/dialog';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import { Input } from 'dashboard/components-next/polaris/ui/input';
import { Textarea } from 'dashboard/components-next/polaris/ui/textarea';
import { Label } from 'dashboard/components-next/polaris/ui/label';

const props = defineProps({
  template: { type: Object, default: null },
  installing: { type: Boolean, default: false },
  errorMessage: { type: String, default: '' },
});

const emit = defineEmits(['submit', 'close']);

const { t } = useI18n();

const open = ref(false);
const values = reactive({});

watch(
  () => props.template,
  template => {
    Object.keys(values).forEach(key => delete values[key]);
    (template?.slots_form || []).forEach(slot => {
      values[slot.key] = slot.default || '';
    });
    open.value = !!template;
  }
);

const isLongText = slot => (slot.max_length || 0) > 240;

const canSubmit = computed(() =>
  (props.template?.slots_form || []).every(
    slot => !slot.required || values[slot.key]?.toString().trim()
  )
);

const close = () => {
  open.value = false;
  emit('close');
};

const submit = () => {
  if (!canSubmit.value || props.installing) return;
  const slots = Object.fromEntries(
    Object.entries(values).filter(([, value]) => value?.toString().trim())
  );
  emit('submit', slots);
};
</script>

<template>
  <Dialog v-model:open="open" @update:open="value => !value && close()">
    <DialogContent v-if="template" class="max-w-lg">
      <DialogHeader>
        <DialogTitle>
          {{ t('POLARIS.TEMPLATES.INSTALL.TITLE', { name: template.name }) }}
        </DialogTitle>
        <DialogDescription>
          {{ t('POLARIS.TEMPLATES.INSTALL.SUBTITLE') }}
        </DialogDescription>
      </DialogHeader>
      <div class="flex flex-col gap-4">
        <div
          v-for="slot in template.slots_form"
          :key="slot.key"
          class="flex flex-col gap-1.5"
        >
          <Label :for="`slot-${slot.key}`">
            {{ slot.key.replaceAll('_', ' ') }}
          </Label>
          <Textarea
            v-if="isLongText(slot)"
            :id="`slot-${slot.key}`"
            v-model="values[slot.key]"
            :maxlength="slot.max_length"
            rows="5"
          />
          <Input
            v-else
            :id="`slot-${slot.key}`"
            v-model="values[slot.key]"
            :maxlength="slot.max_length"
          />
          <p
            v-if="slot.description"
            class="text-xs text-polaris-muted-foreground"
          >
            {{ slot.description }}
          </p>
        </div>
        <p v-if="errorMessage" class="text-sm text-polaris-destructive">
          {{ errorMessage }}
        </p>
      </div>
      <DialogFooter>
        <Button variant="ghost" :disabled="installing" @click="close">
          {{ t('POLARIS.TEMPLATES.INSTALL.CANCEL') }}
        </Button>
        <Button :disabled="!canSubmit || installing" @click="submit">
          {{
            installing
              ? t('POLARIS.TEMPLATES.INSTALL.INSTALLING')
              : t('POLARIS.TEMPLATES.INSTALL.SUBMIT')
          }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
